import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, Wifi, Router, Loader2, X } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';
import { toast } from 'react-toastify';
import DeviceCard from './DeviceCard';
import DeviceListRow from './DeviceListRow';
import TestConnectionBanner from './TestConnectionBanner';
// CSS loaded globally via src/styles/v2/index.css -> pages/integrations-manager.css
// (shared im-* classes) plus pages/controllers-manager.css for the few new ones.

const CONTROLLER_TYPES = [
  { value: 'OMADA', label: 'TP-Link Omada', enabled: true },
  { value: 'UNIFI', label: 'Ubiquiti UniFi', enabled: true },
  { value: 'INCONTROL2', label: 'Peplink InControl2', enabled: true },
  { value: 'MERAKI', label: 'Cisco Meraki', enabled: false },
  { value: 'ARUBA_CENTRAL', label: 'Aruba Central', enabled: false },
];

// Cloud types (currently just INCONTROL2) have no assigned agent and a
// fixed, non-editable base URL -- mirrors the backend's own ON_PREM_TYPES
// in controller.routes.cjs (868m26xrt).
const ON_PREM_TYPES = ['OMADA', 'UNIFI'];
const INCONTROL2_BASE_URL = 'https://api.ic.peplink.com';

const STATUS_LABEL = {
  OK: 'OK',
  AUTH_FAILED: 'Auth failed',
  CONFIG_ERROR: 'Config error',
  UNREACHABLE: 'Unreachable',
  PARTIAL: 'Partial',
  ERROR: 'Error',
};

function formatRelativeTime(date) {
  if (!date) return 'Never';
  const diffMs = Date.now() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

const BASE_URL_PLACEHOLDER = {
  OMADA: 'https://omada.local:8043',
  UNIFI: 'https://<console-ip>',
  INCONTROL2: INCONTROL2_BASE_URL,
};

const EMPTY_FORM = {
  type: 'OMADA', name: '', baseUrl: '', agentId: '', clientId: '', clientSecret: '',
  authMode: 'api_key', apiKey: '', username: '', password: '',
  syncIntervalMin: 15, siteIds: [], externalId: '',
};

function ControllerCard({ controller, onEdit, onDelete, onSyncNow, syncing }) {
  const statusColor = controller.lastSyncStatus === 'OK' ? '#22c55e'
    : controller.lastSyncStatus === 'PARTIAL' ? '#ffaa00'
    : controller.lastSyncStatus ? '#ef4444'
    : '#64748b';
  return (
    <div className="im-card" style={{ '--card-color': statusColor }}>
      <div className="im-card__icon">
        <Router size={18} />
      </div>
      <div className="im-card__body">
        <div className="im-card__name">{controller.name}</div>
        <div className="im-card__type">
          {CONTROLLER_TYPES.find((t) => t.value === controller.type)?.label || controller.type}
          {controller.agent?.name ? ` • ${controller.agent.name}` : ''}
        </div>
        <span className="cm-status-pill" style={{ '--pill-color': statusColor }}>
          {controller.lastSyncStatus ? (STATUS_LABEL[controller.lastSyncStatus] || controller.lastSyncStatus) : 'Never synced'}
        </span>
        <span className="im-card__type">Last sync: {formatRelativeTime(controller.lastSyncAt)}</span>
      </div>
      <div className="im-card__dot" title={controller.enabled ? 'Enabled' : 'Disabled'} />
      <div className="im-card__actions">
        <button className="im-icon-btn" onClick={() => onSyncNow(controller.id)} title="Sync now" disabled={syncing}>
          {syncing ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
        </button>
        <button className="im-icon-btn" onClick={() => onEdit(controller)} title="Edit">
          <Edit2 size={14} />
        </button>
        <button className="im-icon-btn im-icon-btn--danger" onClick={() => onDelete(controller.id)} title="Remove">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

const ControllersManager = () => {
  const [controllers, setControllers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [availableSites, setAvailableSites] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const fetchControllers = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/controllers');
      if (res.ok) {
        const data = await res.json();
        setControllers(data.controllers || []);
      }
    } catch (err) {
      console.error('Failed to load controllers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchControllers();
    fetchWithTenant('/api/md/agents').then((res) => res.ok && res.json()).then((data) => {
      if (data) setAgents(data.agents || data || []);
    }).catch(() => {});
  }, [fetchControllers]);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setAvailableSites(null);
    setTestResult(null);
    setShowForm(true);
  };

  const openEdit = (controller) => {
    setEditingId(controller.id);
    setForm({
      type: controller.type, name: controller.name, baseUrl: controller.baseUrl,
      agentId: controller.agentId || '', clientId: '', clientSecret: '',
      authMode: 'api_key', apiKey: '', username: '', password: '',
      syncIntervalMin: controller.syncIntervalMin, siteIds: controller.siteIds || [],
      externalId: controller.externalId || '',
    });
    setAvailableSites(null);
    setTestResult(null);
    setShowForm(true);
  };

  const closeForm = () => setShowForm(false);

  const handleSave = async () => {
    const agentRequired = !editingId && ON_PREM_TYPES.includes(form.type);
    if (!form.name || !form.baseUrl || (agentRequired && !form.agentId)) {
      toast.error(agentRequired ? 'Name, base URL, and agent are required' : 'Name and base URL are required');
      return;
    }
    // 868m1ytty: an InControl2 org can hold groups unrelated to this
    // tenant, so unlike every other type (empty siteIds = "every site"),
    // this one requires an explicit non-empty selection -- mirrors the
    // backend's PUT /:id rejection. Only checked once editingId is set:
    // the very first save (plain creation, editingId null) still has to
    // succeed with no groups yet, since Test Connection needs the row to
    // exist before any group can even be listed.
    if (editingId && form.type === 'INCONTROL2' && form.siteIds.length === 0) {
      toast.error('Select at least one group before saving an InControl2 controller');
      return;
    }
    setSaving(true);
    try {
      const body = {
        type: form.type, name: form.name, baseUrl: form.baseUrl, agentId: form.agentId || null,
        syncIntervalMin: Number(form.syncIntervalMin) || 15, siteIds: form.siteIds,
      };
      if (form.type === 'UNIFI') {
        body.authMode = form.authMode;
        if (form.authMode === 'local_login') {
          if (form.username && form.password) {
            body.username = form.username;
            body.password = form.password;
          }
        } else if (form.apiKey) {
          body.apiKey = form.apiKey;
        }
      } else if (form.clientId && form.clientSecret) {
        body.clientId = form.clientId;
        body.clientSecret = form.clientSecret;
      }
      if (form.type === 'OMADA') {
        // Omada's Omadac ID (868m3f11g): required in every token request the
        // adapter makes and not derivable from baseUrl/clientId/siteId --
        // unlike INCONTROL2's externalId, which is resolved later via
        // handleSelectOrg after Test Connection, so it's sent here directly
        // rather than left for that flow.
        body.externalId = form.externalId;
      }
      const url = editingId ? `/api/md/controllers/${editingId}` : '/api/md/controllers';
      const res = await fetchWithTenant(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Save failed');
      }
      toast.success(editingId ? 'Controller updated' : 'Controller added');
      setShowForm(false);
      fetchControllers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this controller? Devices it identified keep their category and evidence, just unlinked.')) return;
    try {
      const res = await fetchWithTenant(`/api/md/controllers/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Controller removed');
      fetchControllers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadSites = async () => {
    const sitesRes = await fetchWithTenant(`/api/md/controllers/${editingId}/sites`);
    if (sitesRes.ok) {
      const sitesData = await sitesRes.json();
      setAvailableSites(sitesData.sites || []);
    }
  };

  // handleSelectOrg persists an InControl2 organization id (chosen by the
  // user from testResult.organizations, or auto-selected by handleTest when
  // exactly one was available) via PUT before loading sites -- GET
  // /:id/sites reads the org id off the saved controller row, so it must be
  // saved first (868m26xrt).
  const handleSelectOrg = async (orgId) => {
    if (!editingId || !orgId) return;
    try {
      const res = await fetchWithTenant(`/api/md/controllers/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ externalId: orgId }),
      });
      if (!res.ok) throw new Error('Failed to save the selected organization');
      setForm((f) => ({ ...f, externalId: orgId }));
      await loadSites();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleTest = async () => {
    if (!editingId) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetchWithTenant(`/api/md/controllers/${editingId}/test`, { method: 'POST' });
      const result = await res.json();
      setTestResult(result);
      if (result.ok) {
        if (result.orgId) {
          // Exactly one organization was accessible -- auto-select and save it.
          await handleSelectOrg(result.orgId);
        } else if (!result.organizations) {
          // Not a multi-org InControl2 result (or a non-INCONTROL2 type
          // entirely) -- load sites the way every other controller type does.
          await loadSites();
        }
        // else: several organizations are available -- the Organization
        // select below waits for the user to choose one.
      }
    } catch (err) {
      setTestResult({ ok: false, message: err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSyncNow = async (id) => {
    setSyncingId(id);
    try {
      const res = await fetchWithTenant(`/api/md/controllers/${id}/sync`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Sync failed');
      const s = data.summary || {};
      toast.success(`Sync complete: ${s.matched || 0} matched, ${s.created || 0} created, ${s.clients || 0} wireless clients`);
      fetchControllers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSyncingId(null);
    }
  };

  const toggleSite = (siteId) => {
    setForm((f) => ({
      ...f,
      siteIds: f.siteIds.includes(siteId) ? f.siteIds.filter((s) => s !== siteId) : [...f.siteIds, siteId],
    }));
  };

  if (loading) return null;

  return (
    <div className="im-category-section cm-section">
      <div className="im-category-header" style={{ '--cat-color': '#60a5fa' }}>
        <Wifi size={15} />
        <span>Network Controllers</span>
        {controllers.length > 0 && <span className="im-category-count">{controllers.length}</span>}
      </div>

      {controllers.length === 0 ? (
        <div className="im-empty">No network controllers configured</div>
      ) : (
        <div className="im-cards">
          {controllers.map((c) => (
            <ControllerCard
              key={c.id}
              controller={c}
              onEdit={openEdit}
              onDelete={handleDelete}
              onSyncNow={handleSyncNow}
              syncing={syncingId === c.id}
            />
          ))}
        </div>
      )}

      <button className="im-btn im-btn--ghost cm-add-btn" onClick={openAdd}>
        <Plus size={16} /> Add Controller
      </button>

      {showForm && (
        <div className="im-overlay" onClick={closeForm}>
          <div className="im-wizard" onClick={(e) => e.stopPropagation()}>
            <div className="im-wizard__header">
              <span className="im-wizard__title">{editingId ? 'Edit Controller' : 'Add Controller'}</span>
              <button type="button" className="im-close" onClick={closeForm} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="im-wizard__body">
            <div className="im-fields">
              <DeviceCard className="cm-section-card">
                <span className="cm-section-card__heading">Identity</span>
                <div className="im-field">
                  <label className="im-label">Type <span className="im-required">*</span></label>
                  <select
                    className="im-select"
                    value={form.type}
                    onChange={(e) => {
                      const type = e.target.value;
                      setForm((f) => ({
                        ...f,
                        type,
                        baseUrl: type === 'INCONTROL2' ? INCONTROL2_BASE_URL : (f.type === 'INCONTROL2' ? '' : f.baseUrl),
                      }));
                    }}
                    disabled={!!editingId}
                  >
                    {CONTROLLER_TYPES.map((t) => (
                      <option key={t.value} value={t.value} disabled={!t.enabled}>
                        {t.label}{!t.enabled ? ' (coming soon)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="im-field">
                  <label className="im-label">Name <span className="im-required">*</span></label>
                  <input className="im-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Chabad Omada" />
                </div>
                <div className="im-field">
                  <label className="im-label">Base URL <span className="im-required">*</span></label>
                  <input
                    className="im-input"
                    value={form.baseUrl}
                    onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
                    placeholder={BASE_URL_PLACEHOLDER[form.type] || ''}
                    readOnly={form.type === 'INCONTROL2'}
                    disabled={form.type === 'INCONTROL2'}
                  />
                </div>
                {ON_PREM_TYPES.includes(form.type) && (
                  <div className="im-field">
                    <label className="im-label">Agent <span className="im-required">*</span></label>
                    <select className="im-select" value={form.agentId} onChange={(e) => setForm({ ...form, agentId: e.target.value })}>
                      <option value="">Select agent...</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="im-field">
                  <label className="im-label">Sync Interval (minutes)</label>
                  <input className="im-input" type="number" min="1" value={form.syncIntervalMin} onChange={(e) => setForm({ ...form, syncIntervalMin: e.target.value })} />
                </div>
              </DeviceCard>

              <DeviceCard className="cm-section-card">
                <span className="cm-section-card__heading">Credentials</span>
                {(form.type === 'OMADA' || form.type === 'INCONTROL2') && (
                  <>
                    <div className="im-field">
                      <label className="im-label">Client ID {editingId ? '(leave blank to keep existing)' : <span className="im-required">*</span>}</label>
                      <input className="im-input" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} />
                    </div>
                    <div className="im-field">
                      <label className="im-label">Client Secret {editingId ? '(leave blank to keep existing)' : <span className="im-required">*</span>}</label>
                      <input className="im-input" type="password" value={form.clientSecret} onChange={(e) => setForm({ ...form, clientSecret: e.target.value })} />
                    </div>
                  </>
                )}
                {form.type === 'OMADA' && (
                  <div className="im-field">
                    <label className="im-label">Omadac ID <span className="im-required">*</span></label>
                    <input className="im-input" value={form.externalId} onChange={(e) => setForm({ ...form, externalId: e.target.value })} placeholder="e.g. b7b18100b5dfe2b9cb135fec1da97fda" />
                    <p className="im-hint">From the Omada UI's Open API app list -- click the "view" icon next to your app, shown alongside Interface Access Address.</p>
                  </div>
                )}
                {form.type === 'UNIFI' && (
                  <>
                    <div className="im-field">
                      <label className="im-label">Auth Mode</label>
                      <div className="im-method-pills">
                        {['api_key', 'local_login'].map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            className={`im-method-pill ${form.authMode === mode ? 'im-method-pill--active' : ''}`}
                            onClick={() => setForm({ ...form, authMode: mode })}
                          >
                            {mode === 'api_key' ? 'API Key' : 'Username / Password'}
                          </button>
                        ))}
                      </div>
                    </div>
                    {form.authMode === 'local_login' ? (
                      <>
                        <div className="im-field">
                          <label className="im-label">Username {editingId ? '(leave blank to keep existing)' : <span className="im-required">*</span>}</label>
                          <input className="im-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                        </div>
                        <div className="im-field">
                          <label className="im-label">Password {editingId ? '(leave blank to keep existing)' : <span className="im-required">*</span>}</label>
                          <input className="im-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                        </div>
                      </>
                    ) : (
                      <div className="im-field">
                        <label className="im-label">API Key {editingId ? '(leave blank to keep existing)' : <span className="im-required">*</span>}</label>
                        <input className="im-input" type="password" value={form.apiKey} onChange={(e) => setForm({ ...form, apiKey: e.target.value })} />
                      </div>
                    )}
                  </>
                )}
              </DeviceCard>

              {editingId && (
                <DeviceCard className="cm-section-card">
                  <span className="cm-section-card__heading">Test &amp; Groups</span>
                  <div className="im-field">
                    <button className="im-btn im-btn--ghost" onClick={handleTest} disabled={testing}>
                      {testing ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Test Connection
                    </button>
                  </div>
                  <TestConnectionBanner testing={testing} result={testResult} />
                  {testResult && testResult.organizations && !form.externalId && (
                    <div className="im-field">
                      <label className="im-label">Organization <span className="im-required">*</span></label>
                      <select className="im-select" value="" onChange={(e) => handleSelectOrg(e.target.value)}>
                        <option value="" disabled>Select organization...</option>
                        {testResult.organizations.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {availableSites && (
                    <div className="im-field">
                      {/* 868m1ytty: "none selected = all" is actively wrong
                          for INCONTROL2 -- an org can hold groups unrelated
                          to this tenant, so this type requires an explicit
                          choice instead of defaulting to everything. */}
                      <label className="im-label">
                        {form.type === 'INCONTROL2'
                          ? <>Groups <span className="im-required">*</span></>
                          : 'Sites (none selected = all)'}
                      </label>
                      <DeviceListRow className="cm-site-list">
                        {availableSites.map((s) => (
                          <label key={s.id} className="im-toggle">
                            <input type="checkbox" checked={form.siteIds.includes(s.id)} onChange={() => toggleSite(s.id)} />
                            <span className="im-toggle__slider" />
                            <span className="im-toggle__label">{s.name}</span>
                          </label>
                        ))}
                      </DeviceListRow>
                    </div>
                  )}
                </DeviceCard>
              )}
            </div>
            </div>

            <div className="im-wizard__footer">
              <button className="im-btn im-btn--ghost" onClick={closeForm}>Cancel</button>
              <button className="im-btn im-btn--save" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 size={14} className="spin" /> : null} {editingId ? 'Save' : 'Add Controller'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControllersManager;
